import { render } from 'react-dom'
import './styles.css'
import * as THREE from 'three'
import { useRef, Suspense, useEffect, useState } from 'react'
import { Canvas, useFrame, useThree, extend } from '@react-three/fiber'
import { Center, Bounds, useGLTF, useAnimations } from '@react-three/drei'
import * as Tone from 'tone'
import Colors from './Colors'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass'

let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]
const ci = randI(Colors.length)
const cols = Colors[ci]
const bgColor = cols.shift()
new Array(2).fill().map(() => rand([true, false]) ? cols.shift() : cols.pop())

const feats = window.$fxhashFeatures = {
  explosionAmt: rand([1,2,3,4,5]),
  octave: rand([2,3]),
  camY: randI(3) * 0.003 * rand([1, -1]),
  camX: randI(3) * 0.005,
  pluckLength: rand(['8n','16n','32n'])
}
class AfterBurn extends AfterimagePass {
  constructor() {
    super(1)
    this.shaderMaterial.onBeforeCompile = (shader) => {
      shader.fragmentShader =
        `
        uniform float damp;
        uniform sampler2D tOld;
        uniform sampler2D tNew;
        varying vec2 vUv;
    
        vec4 when_gt( vec4 x, float y ) {
          return max( sign( x - y ), 0.0 );
        }

        void main() {
          vec4 texelOld = texture2D( tOld, vUv );
          vec4 texelNew = texture2D( tNew, vUv );
          texelOld *= damp * when_gt( texelOld, 0.00001 );
          gl_FragColor = max(texelNew, texelOld);
        }
        `
    }
  }
}

extend({ EffectComposer, RenderPass, AfterBurn })
const Tint = () => {
  const ref = useRef()
  const { size, gl, scene, camera } = useThree()
  const composer = useRef()
  useFrame(() => {
    ref.current.uniforms.damp.value = damp
    damp = 1
    composer.current.render()
  }, 1)
  return (
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachFns={['addPass']} args={[scene, camera]} />
      <afterBurn attachFns={['addPass']} args={[size]} ref={ref} />
    </effectComposer>
  )
}
export default function App() {
  const size = useWindowSize();
  useEffect(() => {
    count = 0
  }, [size.width])
  const start = () => {
    if(!ready){
      return
    }
    if (Tone.Transport.state !== 'started') {
      Tone.start();
      Tone.Transport.start();
    }
    let note = notes[count % posMod]
    let note2 = rand(Object.keys(insts))
    let note3 = notes[(count-1) % posMod]
    if(count % posMod === (posMod-2)){
      insts[note].triggerAttackRelease('C'+feats.octave, '8m', '+0.0', 1);
      insts[note2].triggerAttackRelease('C'+feats.octave, '8m', '+0.0', 1);
      insts[note3].triggerAttackRelease('C'+feats.octave, '8m', '+0.0', 1);
    }else{
      insts[note].triggerAttackRelease('C'+feats.octave, feats.pluckLength, '+0.0', 1);
      insts[note3].triggerAttackRelease('C'+feats.octave, feats.pluckLength, '+0.0', 1);
    }
    m = 0
    if ((count+1) % (posMod) === 0) {
      damp = 0
      console.log('now')
    }
    genColz()
    count = count + 1
  }
  return (
    <div id="main" onClick={() => start()} key={size.width}>
      <Canvas flat={true} dpr={[1, 2]} camera={{ near: 0.0001, far: 10, position: [feats.camX, feats.camY, 0.01], fov: 1 }}>
        <Stuff />
        <Back />
        <Tint />
        <ambientLight intensity={0.8} />
        <directionalLight position={[10, 10, 0]} intensity={0.75} />
        <directionalLight position={[-10, 10, 5]} intensity={0.5} />
        <directionalLight position={[-10, 20, 0]} intensity={0.75} />
        <directionalLight position={[0, -10, 0]} intensity={0.65} />
      </Canvas>
    </div>
  )
}

let colz = []
let genColz = () => { colz = [rand(cols), rand(cols)] }
genColz()
function Stuff() {
  const superGroup = useRef()
  const screens = useRef()
  const { camera } = useThree()
  let v = new THREE.Vector3(0)
  useFrame(() => {
    screens.current.scale.setScalar(2 + (((count) % (posMod)) * 2))
  })
  return (
    <group ref={superGroup}>
      <Bounds fit margin={1.4} damping={0}>
        <Center>
          <Bust />
        </Center>
      </Bounds>
      <group ref={screens}>
        <Screens />
      </group>
    </group>
  )
}

let count = 0
let sides = rand([4, 7, 129])
let xScale = 1


function Screens() {
  const ring = useRef()
  const inner = useRef()
  const outer = useRef()
  let s = 0.00012
  useFrame(() => {
    if (count % posMod === 0) {
      outer.current.scale.setScalar(1)
    } else if (count % posMod === (posMod-1)) {
      inner.current.scale.setScalar(1)
      outer.current.scale.setScalar(0)
    } else {
      outer.current.scale.setScalar(1)
      inner.current.scale.setScalar(1)
    }
    ring.current.scale.x = xScale
  })
  return (
    <group ref={ring} position={[0,0,0]}>
      <mesh position={[0, 0, 0]} ref={inner}>
        <meshBasicMaterial depthTest={false} color={bgColor} />
        <circleBufferGeometry args={[s * 1, sides]} />
      </mesh>
      <mesh position={[0, 0, 0]} ref={outer}>
        <meshBasicMaterial depthTest={false} color={bgColor} />
        <ringBufferGeometry args={[s * 1.07, s * 300, sides]} />
      </mesh>
    </group>
  )
}
let m = 0
let ready = false
let ti = 0
let interval = null

function Bust() {
  const ref = useRef()
  const group = useRef()
  const { scene, animations, materials } = useGLTF('./bust.glb')
  const { actions, mixer } = useAnimations(animations, ref)
  useEffect(() => {
    Object.keys(actions).forEach((key) => actions[key].play())
    interval = setInterval(() => {
      count = ti
      ti += 1
      genColz()
      if ((count+1) === (posMod)) {
        ready = true
        clearInterval(interval)
      }
    }, 50)
  }, [])

  useFrame((_,delta) => {
    materials.inner.color.set(colz[0])
    materials.default.color.set(colz[1])
    if(m < feats.explosionAmt){
      m += (0.05)*(0.1*feats.explosionAmt)
    }
    mixer.setTime(m)
    group.current.rotation.y += (count % posMod === posMod - 1) ? 0 : delta * 0.1
    group.current.scale.setScalar(0.9 + ((count % posMod) * 0.01))
    ref.current.position.y = -0.005
    ref.current.position.x = 0.003
  })
  console.log(materials)
  materials.inner.depthWrite = false
  materials.default.depthWrite = false
  let s = 0.001
  return (
    <group ref={group} >
      <primitive scale={[s, s, s]} position={[0,0,0]} rotation={[0, (30+randI(6)) * 0.11, 0]} ref={ref} object={scene} />
    </group>
  )
}

function Back() {
  const ref = useRef();
  useFrame((state) => {
    if (state.clock.getElapsedTime() > 1) {
      ref.current.scale.setScalar(0)
    }
  })
  return (
    <group ref={ref}>
      <mesh>
        <meshBasicMaterial color={bgColor} side={THREE.BackSide} />
        <octahedronBufferGeometry args={[12, 3]} />
      </mesh>
    </group>
  )
}

let damp = 1
document.getElementById('root').style.background = bgColor
render(
  <>
    <Suspense fallback={null}>
      <App />
    </Suspense>
  </>,
  document.getElementById('root')
)
function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowSize;
}


let filters = []
let pans = []
const instNotes = ['G2', 'A2', 'C3', 'D3', 'E3', 'A3', 'G3']
let pansAmt = instNotes.length
let spread = 1.5
new Array(pansAmt).fill().map((_, i) => {
  pans[i] = new Tone.Panner((((spread / pansAmt) * i) - (spread * 0.5)) + (spread / (pansAmt * 2))).toDestination()
  filters[i] = new Tone.Filter(3000 + (10 * randI(10)), "bandpass", -12).connect(pans[i])
})

const insts = {};

instNotes.forEach((n, i) => {
  insts[n] = new Tone.Sampler({ urls: { C3: "./teropa/scale-" + n + ".mp3" } })
  insts[n].release = 1.5
  insts[n].volume.value = 8

  insts[n].connect(filters[i])
})
let posMod = rand([10])

let last = ''
let notes = new Array(posMod).fill().map((_, i) => {
  let note = rand(Object.keys(insts))
  if (note === last) {
    note = rand(Object.keys(insts))
  }
  last = note
  return note
})

console.log('%c🤯🤯 @ramioso 2022-04-29 🤯🤯', 'color: orange');